import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ApiSubjectsManagement from "../../api/subjects-manager";

const useSubjects = (search, filters) => {
  const { hash } = useLocation();

  const [subjects, setSubjects] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const reqIdRef = useRef(0);
  const pageRef = useRef(page);
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  const queryRef = useRef({});

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const routeStatus = useMemo(() => {
    const routeView = hash ? hash.replace("#", "") : "";

    if (routeView === "pending") {
      return "pending";
    }

    if (routeView === "disabled") {
      return "disabled";
    }

    return undefined;
  }, [hash]);

  const queryParams = useMemo(() => {
    const out = {};

    if (search) {
      out.search = search;
    }

    if (filters?.subjectIds?.length) {
      out.subjectIds = filters.subjectIds;
    }

    if (filters?.cohortIds?.length) {
      out.cohortIds = filters.cohortIds;
    }

    if (filters?.siteIds?.length) {
      out.siteIds = filters.siteIds;
    }

    if (routeStatus) {
      out.status = routeStatus;
    } else if (filters?.status) {
      out.status = Array.isArray(filters.status)
        ? filters.status
        : [filters.status];
    }

    return out;
  }, [search, filters, routeStatus]);

  useEffect(() => {
    queryRef.current = queryParams;
  }, [queryParams]);

  const mergeUniqueById = (prev = [], incoming = []) => {
    const seen = new Set(prev.map((item) => item.id));
    return prev.concat(incoming.filter((item) => !seen.has(item.id)));
  };

  const loadPage = useCallback(async (nextPage, { replace = false } = {}) => {
    if (isLoadingRef.current && !replace) return;

    const myReqId = ++reqIdRef.current;
    setLoading(true);
    isLoadingRef.current = true;

    try {
      const { data } = await ApiSubjectsManagement.getSubjects({
        ...queryRef.current,
        page: nextPage,
      });

      if (myReqId !== reqIdRef.current) return;

      const incoming = Array.isArray(data?.subjects) ? data.subjects : [];

      setHasMore(Boolean(data?.hasMore));
      setPage(Number.isFinite(data?.page) ? data.page : nextPage);
      setSubjects((prev) =>
        replace ? incoming : mergeUniqueById(prev, incoming)
      );
    } finally {
      if (myReqId === reqIdRef.current) {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, []);

  const fetchNext = useCallback(() => {
    if (!hasMoreRef.current || isLoadingRef.current) return;
    return loadPage(pageRef.current + 1);
  }, [loadPage]);

  const resetAndFetch = useCallback(async () => {
    setSubjects([]);
    setPage(0);
    setHasMore(true);
    await loadPage(1, { replace: true });
  }, [loadPage]);

  const upsertSubject = useCallback((subject) => {
    if (!subject?.id) return;

    setSubjects((prev) =>
      (prev || []).map((item) =>
        item.id === subject.id ? { ...item, ...subject } : item
      )
    );
  }, []);

  const removeSubjects = useCallback((ids) => {
    const idSet = new Set((ids || []).filter(Boolean));
    if (!idSet.size) return;

    setSubjects((prev) => (prev || []).filter((item) => !idSet.has(item.id)));
  }, []);

  useEffect(() => {
    resetAndFetch();
  }, [resetAndFetch, queryParams]);

  return {
    subjects,
    hasMore,
    isLoading,
    fetchNext,
    resetAndFetch,
    upsertSubject,
    removeSubjects,
  };
};

export default useSubjects;
